'use client';

import { type FC, type ReactNode, useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import useStickyNavigation from '@/utils/hooks/useStickyNavigation';

import {
    TabsList,
    TabsRoot,
    TabsTrigger,
    type TabsTriggerProps,
} from '@/components/molecules/Tabs';
import { navigationRef } from '@/components/organisms/Navigation';

interface StickyTabsProps {
    tabs: TabsTriggerProps[];
    children: ReactNode;
}

const StickyTabs: FC<StickyTabsProps> = ({ tabs, children }) => {
    const [isSticky, setIsSticky] = useState<boolean>(false);
    const [isMobile, setIsMobile] = useState(false);
    const isHeaderVisible = useStickyNavigation();

    const tabsListRef = useRef<HTMLDivElement>(null);
    const tabsRef = useRef<HTMLDivElement>(null);

    if (tabsListRef?.current && isMobile) {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.intersectionRatio) {
                    const element = entry.target as HTMLDivElement;

                    setIsSticky(entry.intersectionRatio < 1 || false);

                    if (entry.intersectionRatio < 1 && isHeaderVisible) {
                        element.style.padding = `${(navigationRef.current?.clientHeight || 0) + 12}px 0 12px`;
                    } else {
                        element.style.padding = `12px 0`;
                    }
                }
            },
            { threshold: [1] },
        );

        observer.observe(tabsListRef.current);
    }

    useEffect(() => {
        if (window?.innerWidth) {
            setIsMobile(window?.innerWidth < 768);
        }
    }, []);

    return (
        <TabsRoot
            ref={tabsRef}
            defaultValue={tabs[0]?.value}
            onValueChange={() => {
                if (isSticky) {
                    tabsRef?.current?.scrollIntoView({
                        behavior: 'smooth',
                    });
                }
            }}
        >
            <TabsList
                ref={tabsListRef}
                className={cn(
                    'duration-[300ms] sticky top-[-1px] z-20 w-full rounded-none bg-background py-3 transition-[padding]',
                    'sm:static md:z-auto',
                    isSticky && 'border-b-[1px] border-b-grey-100',
                )}
            >
                {tabs.map(({ value, title, ...props }) => (
                    <TabsTrigger
                        key={value}
                        value={value}
                        size="m"
                        {...props}
                    >
                        {title}
                    </TabsTrigger>
                ))}
            </TabsList>

            {children}
        </TabsRoot>
    );
};

export default StickyTabs;
